import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  IconButton,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Box,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SchoolIcon from "@mui/icons-material/School";
import { useTranslation } from "react-i18next";
import { useError } from "../../contexts/ErrorContext";
import { useAuth } from "../../contexts/AuthContext";
import { useStudents, Student, StudentRollScholarUpdateDto } from "../../hooks/useStudents";
import { useSessionClass } from "../../contexts/SessionClassContext";

interface RollNumberModalProps {
  open: boolean;
  handleClose: () => void;
  onSubmit: (students: StudentRollScholarUpdateDto[]) => void;
}

const RollNumberModal: React.FC<RollNumberModalProps> = ({
  open,
  handleClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useError();
  const { user, isTeacher } = useAuth();
  const { selectedSession: contextSession, selectedClass: contextClass } = useSessionClass();

  // Get schoolId
  const schoolId = useMemo(() => {
    if (isTeacher()) {
      return user?.schoolId;
    }
    return user?.schoolId;
  }, [user?.schoolId, isTeacher]);

  // Convert null to undefined for hooks
  const schoolIdForHooks = useMemo(() => {
    return schoolId ?? undefined;
  }, [schoolId]);

  // Use context values directly - they are already selected
  const selectedSessionId = contextSession?.id ?? null;
  const selectedClassId = contextClass?.id ?? null;
  const className = contextClass
    ? `${contextClass.name} ${contextClass.section || ""}`.trim()
    : "";
  const sessionYear = contextSession?.name ?? "";

  // Normalize IDs for the hook
  const normalizedSessionId = selectedSessionId ?? undefined;
  const normalizedClassId = selectedClassId ?? undefined;

  // Fetch students using filtered API when both session and class are selected
  const { useStudentsBySessionIdAndClassId, studentsQuery } = useStudents(false, schoolIdForHooks);
  
  // Use filtered API query when both session and class are selected
  const { 
    data: filteredStudentsFromAPI = [], 
    isLoading: isLoadingFilteredStudents, 
    error: errorFilteredStudents 
  } = useStudentsBySessionIdAndClassId(
    normalizedSessionId,
    normalizedClassId
  );
  
  // Fallback to all students when session/class are not selected
  const allStudents = studentsQuery.data || [];
  const isLoadingAllStudents = studentsQuery.isLoading;
  
  // State for roll numbers - keyed by student id
  const [rollNumbers, setRollNumbers] = useState<Record<number, string>>({});

  // Use filtered students from API when both session and class are selected, otherwise filter all students
  const filteredStudents = useMemo(() => {
    // If both session and class are selected, use the filtered API results
    if (normalizedSessionId && normalizedClassId) {
      return (filteredStudentsFromAPI || [])
        .filter((student) => student.status !== false)
        .sort((a, b) => {
          const nameA = a.studentName || "";
          const nameB = b.studentName || "";
          return nameA.localeCompare(nameB);
        });
    }

    // Otherwise, use the old filtering logic for backward compatibility
    if (!selectedSessionId || !selectedClassId || !allStudents) {
      return [];
    }

    return allStudents.filter((student) => {
      const studentClassId = student.schoolClassId;
      const classMatch = Number(studentClassId) === Number(selectedClassId);
      const sessionMatch = Number(student.sessionId) === Number(selectedSessionId);
      const isActive = student.status !== false;
      return classMatch && sessionMatch && isActive;
    }).sort((a, b) => {
      const nameA = a.studentName || "";
      const nameB = b.studentName || "";
      return nameA.localeCompare(nameB);
    });
  }, [
    normalizedSessionId, 
    normalizedClassId, 
    filteredStudentsFromAPI, 
    allStudents, 
    selectedSessionId, 
    selectedClassId
  ]);

  // Initialize roll numbers when modal opens - preserve user input
  useEffect(() => {
    if (open && filteredStudents.length > 0) {
      // Only initialize roll numbers that don't already exist in state
      // This preserves user input when the student list updates
      setRollNumbers((prev) => {
        const updated: Record<number, string> = { ...prev };
        filteredStudents.forEach((student) => {
          // Only set if not already in state (preserves user input)
          if (!(student.id in updated) && student.rollNumber) {
            updated[student.id] = student.rollNumber;
          }
        });
        return updated;
      });
    } else if (open && filteredStudents.length === 0) {
      // Clear if no students
      setRollNumbers({});
    }
  }, [open]); // Only run when modal opens/closes, not when filteredStudents changes

  // Update roll number for a student
  const handleRollNumberChange = useCallback((studentId: number, value: string) => {
    setRollNumbers((prev) => ({
      ...prev,
      [studentId]: value,
    }));
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault();

      if (!selectedSessionId) {
        showError(t("please_select_a_session") || "Please select a session");
        return;
      }

      if (!selectedClassId) {
        showError(t("please_select_a_class") || "Please select a class");
        return;
      }

      if (filteredStudents.length === 0) {
        showError(t("no_students_found") || "No students found for selected class and session");
        return;
      }

      // Prepare students data - only include students with filled roll numbers
      const studentsToUpdate: StudentRollScholarUpdateDto[] = filteredStudents
        .filter((student) => {
          const rollNo = rollNumbers[student.id]?.trim();
          return rollNo && rollNo !== "";
        })
        .map((student) => ({
          id: student.id,
          schoolClassId: selectedClassId,
          sessionId: selectedSessionId,
          mobileNo: student.mobileNo || "", // Send mobile number in payload but don't show in UI
          scholarNo: "", // Empty for roll number functionality
          rollNumber: rollNumbers[student.id]?.trim() || "",
        }));

      if (studentsToUpdate.length === 0) {
        showError(t("please_fill_at_least_one_roll_number") || "Please fill at least one roll number");
        return;
      }

      onSubmit(studentsToUpdate);
    },
    [selectedSessionId, selectedClassId, filteredStudents, rollNumbers, onSubmit, showError, t]
  );

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason !== "backdropClick" && reason !== "escapeKeyDown") {
          handleClose();
        }
      }}
      maxWidth="lg"
      fullWidth
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          backgroundColor: "#f8f9fb",
          borderBottom: "2px solid #e0e7ff",
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          {t("add_roll_number") || "Add Roll Number"}
        </Typography>
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 4, bgcolor: '#f8fafc' }}>
        <form onSubmit={handleSubmit}>
          {/* Class and Session Information Section */}
          <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <SchoolIcon sx={{ color: "#3092e3" }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: "#3092e3" }}>
                  {t("class_and_session") || "Class and Session"}
                </Typography>
              </Box>
              
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("class")}
                    value={className}
                    fullWidth
                    InputProps={{ 
                      readOnly: true,
                      startAdornment: <SchoolIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f5f5f5'
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <TextField
                    label={t("session_year") || "Session Year"}
                    value={sessionYear}
                    fullWidth
                    InputProps={{ 
                      readOnly: true,
                      startAdornment: <SchoolIcon sx={{ mr: 1, color: 'text.secondary' }} />
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: '#f5f5f5'
                      }
                    }}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Roll Number Entry Section */}
          <Card sx={{ mb: 2, borderRadius: 2, boxShadow: '0 4px 12px rgba(0,0,0,0.05)' }}>
            <CardContent sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={3}>
                <SchoolIcon sx={{ color: "#3092e3" }} />
                <Typography variant="h6" fontWeight="bold" sx={{ color: "#3092e3" }}>
                  {t("roll_number_entry") || "Roll Number Entry"}
                </Typography>
              </Box>

          {((normalizedSessionId && normalizedClassId && isLoadingFilteredStudents) || ((!normalizedSessionId || !normalizedClassId) && isLoadingAllStudents)) ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredStudents.length > 0 ? (
            <TableContainer component={Paper} sx={{ maxHeight: 500 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>S.No</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>{t("student_name") || "Student Name"}</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>{t("father_name") || "Father Name"}</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>{t("roll_number") || "Roll Number"}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStudents.map((student, index) => (
                    <TableRow key={student.id}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{student.studentName || "-"}</TableCell>
                      <TableCell>{student.fatherName || "-"}</TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={rollNumbers[student.id] || ""}
                          onChange={(e) => handleRollNumberChange(student.id, e.target.value)}
                          placeholder={t("enter_roll_number") || "Enter Roll Number"}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : selectedSessionId && selectedClassId ? (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t("no_students_found_for_selected_class_and_session") || "No students found for selected class and session"}
            </Alert>
          ) : null}
            </CardContent>
          </Card>
        </form>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: "1px solid #e0e0e0" }}>
        <Button onClick={handleClose} color="inherit">
          {t("cancel") || "Cancel"}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedSessionId || !selectedClassId || filteredStudents.length === 0}
          sx={{
            backgroundColor: "#3092e3",
            "&:hover": { backgroundColor: "primary.main" },
          }}
        >
          {t("submit") || "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default RollNumberModal;

